import React, { useEffect, useState, useRef } from 'react';
import html2pdf from 'html2pdf.js';
import logo from '../Images/logo-main-black.png';
import './receipt.css';
import { formatMoney } from '../utils/currency';
import { extractPublicDocumentLookup, fetchPublicDocument, formatApiDate } from '../utils/publicDocsApi';

function ViewReceipt() {
  const [receiptData, setReceiptData] = useState(null);
  const [studentName, setStudentName] = useState('');
  const [admissionNumber, setAdmissionNumber] = useState('');
  const [currencyInfo, setCurrencyInfo] = useState({ formattedAmount: '', displayCurrency: 'KES' });
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [qrUnavailable, setQrUnavailable] = useState(false);
  const receiptRef = useRef();
  const lookup = extractPublicDocumentLookup(window.location.search);
  const receiptUrl = window.location.href;
  const qrCodeUrl = `https://api.qrserver.com/v1/create-qr-code/?size=220x220&margin=0&data=${encodeURIComponent(receiptUrl)}`;

  useEffect(() => {
    const fetchReceipt = async () => {
      setIsLoading(true);
      setLoadError('');
      setQrUnavailable(false);

      if (!lookup) {
        setLoadError('The receipt link is invalid.');
        setIsLoading(false);
        return;
      }

      try {
        const payload = await fetchPublicDocument('receipts', lookup);
        const nextReceipt = payload?.receipt || null;
        const nextStudent = payload?.student || {};
        const nextCurrency = payload?.currency || {};

        setReceiptData(nextReceipt);
        setStudentName(nextStudent.displayName || nextStudent.bio || '');
        setAdmissionNumber(nextStudent.admissionNumber || '');
        setCurrencyInfo({
          formattedAmount: nextCurrency.formattedAmount || formatMoney(nextCurrency.displayAmount, nextCurrency.displayCurrency || nextReceipt?.currency || 'KES'),
          displayCurrency: nextCurrency.displayCurrency || nextReceipt?.currency || 'KES',
        });
      } catch (error) {
        console.error('Error fetching receipt:', error);
        setLoadError(error.message || 'We could not load this receipt right now.');
      } finally {
        setIsLoading(false);
      }
    };

    fetchReceipt();
  }, [lookup]);

  const exportPDF = () => {
    if (!receiptRef.current) return;
    html2pdf()
      .from(receiptRef.current)
      .set({
        margin: 10,
        filename: `${receiptData?.invoiceNumber || lookup || 'receipt'}_receipt.pdf`,
        image: { type: 'jpeg', quality: 0.98 },
        html2canvas: { scale: 2, useCORS: true },
        jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' },
      })
      .save();
  };

  if (isLoading) {
    return (
      <div className="receiptPage">
        <div className="receiptStateCard">
          <p className="receiptStateEyebrow">Payment receipt</p>
          <h2>Loading receipt...</h2>
          <p className="mb-0">Please wait while we prepare the payment details.</p>
        </div>
      </div>
    );
  }

  if (loadError || !receiptData) {
    return (
      <div className="receiptPage">
        <div className="receiptStateCard">
          <p className="receiptStateEyebrow">Payment receipt</p>
          <h2>Receipt unavailable</h2>
          <p className="mb-0">{loadError || 'We could not find the requested receipt.'}</p>
        </div>
      </div>
    );
  }

  const formattedPaidAmount = currencyInfo.formattedAmount || formatMoney(receiptData.displayAmount || receiptData.amountInKes || receiptData.amount, currencyInfo.displayCurrency);
  const conversionNote = String(receiptData.conversionNote || '').trim();

  return (
    <div className="receiptPage">
      <div className="receiptShell">
        <>
          <div
            ref={receiptRef}
            className="receiptCard"
          >
            <div className="receiptHeader">
              <div className="receiptBrand">
                <img src={logo} className="receiptBrandLogo" alt="Ubunifu College" />
                <div>
                  <p className="receiptEyebrow">Ubunifu College</p>
                  <h1>Payment Receipt</h1>
                  <p className="receiptMuted mb-0">Official confirmation of fee payment</p>
                </div>
              </div>
              <div className="receiptHeaderMeta receiptDocumentMeta">
                <div className="receiptMetaStack">
                  <span className="receiptMetaLabel">Receipt No.</span>
                  <strong>{receiptData.invoiceNumber}</strong>
                </div>
                <div className="receiptMetaStack">
                  <span className="receiptMetaLabel">Date</span>
                  <strong>{formatApiDate(receiptData.paymentDate)}</strong>
                </div>
              </div>
            </div>

            <div className="receiptDivider" />

            <div className="receiptTopGrid">
              <div className="receiptPartyBlock">
                <p className="receiptSectionEyebrow">Received from</p>
                <h2>{studentName || 'Student record unavailable'}</h2>
                <p className="receiptPartyLine">Admission No. {admissionNumber || '--'}</p>
                <div className="receiptMetaList">
                  <div className="receiptMetaListRow">
                    <span>Payment method</span>
                    <strong>{receiptData.paymentMethod || 'Not provided'}</strong>
                  </div>
                  {conversionNote ? (
                    <div className="receiptMetaListRow">
                      <span>Conversion</span>
                      <strong>{conversionNote}</strong>
                    </div>
                  ) : null}
                </div>
              </div>

              <div className="receiptQrPanel">
                <p className="receiptSectionEyebrow">Verification</p>
                <div className="receiptQrFrame">
                  {qrUnavailable ? (
                    <div className="receiptQrFallback">
                      <span>Verification link</span>
                      <strong>{receiptData.invoiceNumber}</strong>
                    </div>
                  ) : (
                    <img
                      src={qrCodeUrl}
                      alt={`QR code for receipt ${receiptData.invoiceNumber}`}
                      className="receiptQrImage"
                      crossOrigin="anonymous"
                      onError={() => setQrUnavailable(true)}
                    />
                  )}
                </div>
                <p className="receiptQrCaption mb-0">Scan to open this receipt online.</p>
              </div>
            </div>

            <div className="receiptSnapshotGrid">
              <div className="receiptSnapshotItem">
                <span className="receiptMetaLabel">Amount paid</span>
                <strong>{formattedPaidAmount}</strong>
              </div>
              <div className="receiptSnapshotItem">
                <span className="receiptMetaLabel">Payment date</span>
                <strong>{formatApiDate(receiptData.paymentDate)}</strong>
              </div>
              <div className="receiptSnapshotItem">
                <span className="receiptMetaLabel">Receipt reference</span>
                <strong>{receiptData.invoiceNumber}</strong>
              </div>
            </div>

            <div className="receiptTableWrap">
              <table className="table receiptTable mb-0">
                <thead>
                  <tr>
                    <th>Description</th>
                    <th>Payment Method</th>
                    <th className="text-end">Amount</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td data-label="Description">Fee Payment</td>
                    <td data-label="Payment Method">{receiptData.paymentMethod || 'Not provided'}</td>
                    <td data-label="Amount" className="text-end">{formattedPaidAmount}</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div className="receiptTotalBar">
              <span>Total paid</span>
              <strong>{formattedPaidAmount}</strong>
            </div>

            <div className="receiptFooter">
              <div className="receiptFooterMeta">
                <span>This receipt is computer-generated and does not require a signature.</span>
                <strong>Reference: {receiptData.invoiceNumber}</strong>
              </div>
              <p className="mb-0">Keep this receipt for your records and future fee reconciliation.</p>
            </div>
          </div>

          <div className="receiptActions">
            <button onClick={exportPDF} className="receiptDownloadBtn">
              Download as PDF
            </button>
          </div>
        </>
      </div>
    </div>
  );
}

export default ViewReceipt;
